import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function Hero() {
  const { t, lang } = useLang();
  const [settings, setSettings] = useState(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/settings"); setSettings(data || {}); }
    catch (e) { setSettings({}); }
  })(); }, []);

  const s = settings || {};
  const pick = (key) => (lang === "zh" ? s[`${key}_zh`] || s[`${key}_en`] : s[`${key}_en`]);
  const eyebrow = pick("hero_eyebrow") || t.hero.eyebrow;
  const titleA = pick("hero_title_a") || t.hero.title_a;
  const titleB = pick("hero_title_b") || t.hero.title_b;
  const sub = pick("hero_sub") || t.hero.sub;
  const stats = (s.hero_stats && s.hero_stats.length) ? s.hero_stats : [
    { n: "8+", label: "yrs studio" }, { n: "96%", label: "exam pass" }, { n: "1:6", label: "cohort cap" },
  ];

  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="grain-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))] mb-6" data-testid="hero-eyebrow">{eyebrow}</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-medium text-[hsl(var(--foreground))]">
              {titleA}
              <br />
              <span className="text-[hsl(var(--primary))]">{titleB}</span>
            </h1>
            <p className="mt-7 max-w-xl text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">{sub}</p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" data-testid="hero-book-btn" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full px-6 lift">
                <a href="#booking">{t.hero.cta_book}<ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-programs-btn" className="rounded-full border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--muted))]">
                <a href="#programs">{t.hero.cta_explore}</a>
              </Button>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {stats.slice(0, 3).map((stat, i) => (
                <div key={i}>
                  <div className="font-display text-3xl md:text-4xl tracking-tight font-medium text-[hsl(var(--foreground))]">{stat.n}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[hsl(var(--border))]">
              <img src="https://images.unsplash.com/photo-1486718448742-163732cd1544?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Architectural dawn" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
              <div className="absolute bottom-4 left-4 right-4 backdrop-blur-md bg-white/40 rounded-xl p-4 border border-white/50">
                <p className="text-[10px] tracking-[0.25em] text-[hsl(var(--foreground))]/70">SINCE 2018</p>
                <p className="font-display text-lg leading-tight text-[hsl(var(--foreground))]">Small cohorts. Careful teaching. Honest progress.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
